/**
 * @module HypothesisTimeline
 * @purpose Renders the per-hypothesis timeline rail from contextEnvelope.timeline + timelineWindow.
 * @depends types/reasoning (HypothesisTimelineEvent, TimelineWindow), react-i18next
 * @dependedOnBy HypothesisWalkthrough (step 4)
 *
 * @rules
 * - Performance: pure display — props only, no state, no data fetching
 * - State: none
 * - Errors: n/a — purely presentational; events are pre-sorted by backend, never re-sorted
 */

import { useTranslation } from 'react-i18next'
import type { HypothesisTimelineEvent, TimelineWindow } from '../../../../../types/reasoning'

interface HypothesisTimelineProps {
  events: HypothesisTimelineEvent[]
  window: TimelineWindow | null
}

interface TimelineNodeProps {
  event: HypothesisTimelineEvent
}

const CONTAINER_STYLE: React.CSSProperties = {
  padding: '4px 0',
}

const SECTION_HEADING: React.CSSProperties = {
  fontFamily: 'var(--font-mono)',
  fontSize: '0.62rem',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  color: 'var(--text-muted)',
  marginBottom: '8px',
}

const RAIL_WRAPPER: React.CSSProperties = {
  position: 'relative',
  paddingLeft: '20px',
}

const RAIL_LINE: React.CSSProperties = {
  position: 'absolute',
  left: '7px',
  top: '4px',
  bottom: '4px',
  width: '2px',
  background: 'var(--border)',
}

const NODE_WRAPPER: React.CSSProperties = {
  position: 'relative',
  padding: '6px 0 6px 14px',
}

const NODE_DOT_BASE: React.CSSProperties = {
  position: 'absolute',
  left: '-2px',
  top: '10px',
  width: '10px',
  height: '10px',
  borderRadius: '50%',
  background: 'var(--bg-card)',
  border: '2px solid var(--text-muted)',
}

const NODE_DOT_HIT: React.CSSProperties = {
  ...NODE_DOT_BASE,
  background: 'var(--accent)',
  border: '2px solid var(--accent)',
}

const TS_STYLE: React.CSSProperties = {
  fontFamily: 'var(--font-mono)',
  fontSize: '0.6rem',
  color: 'var(--text-muted)',
}

const BASENAME_BASE: React.CSSProperties = {
  fontFamily: 'var(--font-body)',
  fontSize: '0.78rem',
  color: 'var(--text)',
  fontWeight: 500,
}

const BASENAME_HIT: React.CSSProperties = {
  ...BASENAME_BASE,
  color: 'var(--accent)',
}

const EVENT_TYPE_STYLE: React.CSSProperties = {
  fontFamily: 'var(--font-mono)',
  fontSize: '0.58rem',
  color: 'var(--text-muted)',
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  marginLeft: '6px',
}

const NOTE_STYLE: React.CSSProperties = {
  fontFamily: 'var(--font-body)',
  fontSize: '0.7rem',
  color: 'var(--text-dim)',
  marginTop: '2px',
  fontStyle: 'italic',
}

const WINDOW_CAPTION: React.CSSProperties = {
  fontFamily: 'var(--font-mono)',
  fontSize: '0.6rem',
  color: 'var(--text-muted)',
  marginTop: '10px',
  paddingTop: '8px',
  borderTop: '1px solid var(--border)',
}

const EMPTY_STYLE: React.CSSProperties = {
  fontFamily: 'var(--font-body)',
  fontSize: '0.78rem',
  color: 'var(--text-muted)',
  fontStyle: 'italic',
}

function TimelineNode({ event }: TimelineNodeProps): React.ReactElement {
  const { t } = useTranslation('analysis')
  const eventLabel = t(`reasoning.hypothesis.timeline.eventTypes.${event.event_type}` as never, {
    defaultValue: event.event_type,
  })
  return (
    <div style={NODE_WRAPPER}>
      <div style={event.is_hit ? NODE_DOT_HIT : NODE_DOT_BASE} />
      <div style={TS_STYLE}>{event.ts}</div>
      <div>
        <span style={event.is_hit ? BASENAME_HIT : BASENAME_BASE}>{event.basename}</span>
        <span style={EVENT_TYPE_STYLE}>{eventLabel}</span>
      </div>
      {event.note !== null && <div style={NOTE_STYLE}>{event.note}</div>}
    </div>
  )
}

/**
 * Renders the timeline rail from context envelope events and window.
 *
 * @behaviour Displays events in the order returned by the backend (pre-sorted).
 *   Hit nodes (is_hit=true) are highlighted with the accent colour.
 *   Window start/end + derived_from shown as a caption below the rail.
 *   Empty state renders a neutral message.
 * @errors n/a
 */
/**
 * Maximum timeline nodes committed to the DOM.
 *
 * The context envelope is unbounded -- a 60k-artefact case produced 86,663
 * events in a single hypothesis. Rendering them all locked the main thread and
 * looked to users like the step was stuck processing forever. Capped per the
 * architecture rule: at most 100 rows in the DOM regardless of dataset size.
 */
const MAX_TIMELINE_NODES = 100

export function HypothesisTimeline({ events, window: timelineWindow }: HypothesisTimelineProps): React.ReactElement {
  const { t } = useTranslation('analysis')
  const visibleEvents = events.slice(0, MAX_TIMELINE_NODES)

  return (
    <div style={CONTAINER_STYLE}>
      <div style={SECTION_HEADING}>{t('reasoning.hypothesis.timeline.title')}</div>
      {events.length === 0
        ? <div style={EMPTY_STYLE}>{t('reasoning.hypothesis.timeline.empty')}</div>
        : (
          <>
            <div style={RAIL_WRAPPER}>
              <div style={RAIL_LINE} />
              {visibleEvents.map((event, index) => (
                <TimelineNode key={event.observation_id ?? `evt-${String(index)}`} event={event} />
              ))}
            </div>
            {events.length > visibleEvents.length && (
              <div style={WINDOW_CAPTION}>
                {t('reasoning.hypothesis.timeline.truncated', {
                  shown: visibleEvents.length,
                  total: events.length,
                })}
              </div>
            )}
            {timelineWindow !== null && (
              <div style={WINDOW_CAPTION}>
                {timelineWindow.start !== null && timelineWindow.end !== null
                  ? t('reasoning.hypothesis.timeline.windowCaption', {
                    start: timelineWindow.start,
                    end: timelineWindow.end,
                  })
                  : t('reasoning.hypothesis.timeline.windowNoBounds')
                }
                {timelineWindow.derived_from !== 'none' && (
                  <>
                    {' — '}
                    {t('reasoning.hypothesis.timeline.windowDerivedFrom', {
                      source: timelineWindow.derived_from,
                    })}
                  </>
                )}
              </div>
            )}
          </>
        )
      }
    </div>
  )
}
